import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BRAND = "NIVESH STREET".split("");
const TAGLINE = "LIVE MARKET INTELLIGENCE";

const TICKER_ITEMS = [
  { sym: "NIFTY 50", val: "25,181.80", chg: "+1.38%", up: true },
  { sym: "SENSEX", val: "72,472.33", chg: "+1.23%", up: true },
  { sym: "BANK NIFTY", val: "55,256.65", chg: "+1.36%", up: true },
  { sym: "INDIA VIX", val: "14.38", chg: "-0.42%", up: false },
  { sym: "GOLD", val: "₹1,21,540", chg: "+0.61%", up: true },
  { sym: "BTC/USD", val: "$112,480", chg: "+2.14%", up: true },
  { sym: "USD/INR", val: "88.42", chg: "-0.08%", up: false },
  { sym: "CRUDE", val: "$84.16", chg: "+0.94%", up: true },
];

/**
 * Signature boot intro — plays once per browser session.
 * Letters stagger in terminal-style, a live-ticker strip rolls beneath,
 * then the entire dark screen sweeps upward to reveal the site.
 * Click anywhere to skip.
 */
export default function IntroSplash() {
  const [show, setShow] = useState(() => {
    try {
      return !sessionStorage.getItem("ns_intro_seen");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      try { sessionStorage.setItem("ns_intro_seen", "1"); } catch {}
      setShow(false);
    }, 2400);
    return () => clearTimeout(t);
  }, [show]);

  const dismiss = () => {
    try { sessionStorage.setItem("ns_intro_seen", "1"); } catch {}
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro-splash"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center cursor-pointer select-none"
          style={{ background: "#12100C" }}
          onClick={dismiss}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
          data-testid="intro-splash"
        >
          {/* scanline sweep */}
          <motion.div
            className="absolute left-0 right-0 h-px bg-[#D91E2A]/60"
            initial={{ top: "0%", opacity: 0 }}
            animate={{ top: "100%", opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.6, ease: "linear" }}
          />

          {/* LIVE badge */}
          <motion.div
            className="flex items-center gap-2 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D91E2A] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D91E2A]" />
            </span>
            <span className="text-[10px] font-mono-fin tracking-[0.35em] text-[#D91E2A] font-bold">LIVE</span>
          </motion.div>

          {/* brand letters stagger in */}
          <div className="flex overflow-hidden">
            {BRAND.map((ch, i) => (
              <motion.span
                key={i}
                className="font-heading font-extrabold text-[#F7F4EE] text-4xl sm:text-6xl lg:text-7xl tracking-tight"
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 + i * 0.045, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {ch === " " ? "\u00A0" : ch}
              </motion.span>
            ))}
            {/* blinking terminal cursor */}
            <motion.span
              className="font-heading font-extrabold text-[#D91E2A] text-4xl sm:text-6xl lg:text-7xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0, 1] }}
              transition={{ delay: 0.25 + BRAND.length * 0.045, duration: 0.9, repeat: Infinity, repeatDelay: 0.2 }}
            >
              _
            </motion.span>
          </div>

          {/* red rule grows */}
          <motion.div
            className="h-[2px] bg-[#D91E2A] mt-5"
            initial={{ width: 0 }}
            animate={{ width: 180 }}
            transition={{ delay: 0.9, duration: 0.5, ease: "easeOut" }}
          />

          {/* tagline */}
          <motion.div
            className="mt-4 text-[11px] sm:text-xs font-mono-fin tracking-[0.45em] text-[#8C8D90]"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.4 }}
          >
            {TAGLINE}
          </motion.div>

          {/* rolling market ticker strip */}
          <motion.div
            className="absolute bottom-10 left-0 right-0 overflow-hidden border-y border-[#F7F4EE]/10 py-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15 }}
          >
            <motion.div
              className="flex gap-10 whitespace-nowrap w-max"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 18, ease: "linear", repeat: Infinity }}
            >
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
                <span key={i} className="font-mono-fin text-[11px] tracking-wider">
                  <span className="text-[#8C8D90]">{t.sym}</span>{" "}
                  <span className="text-[#F7F4EE] font-semibold">{t.val}</span>{" "}
                  <span className={t.up ? "text-emerald-400" : "text-rose-400"}>{t.chg}</span>
                </span>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
